import type { BuildSettlement } from './settlement-build-api';
import { BrowserApiError, browserApiClient, requiredApiData } from './browser-api';

export interface PlacementDraft {
  buildingId: string;
  x: number;
  y: number;
  districtCode: string;
  rotation: number;
}

export function placementFootprint(
  building: BuildSettlement['buildings'][number],
  rotation: number,
): { footprintWidth: number; footprintHeight: number } {
  // API dimensions already include the saved orientation. Only a parity change
  // relative to that orientation swaps them; never rotate server dimensions twice.
  const swapped = Math.abs(rotation - building.rotation) % 2 === 1;
  return {
    footprintWidth: swapped ? building.footprint_height : building.footprint_width,
    footprintHeight: swapped ? building.footprint_width : building.footprint_height,
  };
}

// Presentation guidance only. The server validates the current layout under a lock.
export function placementProblem(
  settlement: BuildSettlement,
  draft: PlacementDraft,
): string | undefined {
  const building = settlement.buildings.find((item) => item.id === draft.buildingId);
  if (!building) return 'This building is no longer available.';
  if (building.state === 'constructing') return 'Finish construction before moving this building.';
  if (!Number.isInteger(draft.rotation) || draft.rotation < 0 || draft.rotation > 3)
    return 'Choose a valid building orientation.';
  const { footprintWidth, footprintHeight } = placementFootprint(building, draft.rotation);
  const district = settlement.districts.find(
    (item) => item.code === draft.districtCode && item.unlocked_at,
  );
  if (!district) return 'Choose an unlocked district.';
  const { x, y } = draft;
  if (
    !Number.isInteger(x) ||
    !Number.isInteger(y) ||
    x < -50 ||
    x > 50 ||
    y < -50 ||
    y > 50 ||
    x < district.min_x ||
    y < district.min_y ||
    x + footprintWidth - 1 > district.max_x ||
    y + footprintHeight - 1 > district.max_y
  )
    return 'Keep the whole building inside the highlighted district.';
  if (
    settlement.buildings.some(
      (other) =>
        other.id !== building.id &&
        x < other.x + other.footprint_width &&
        x + footprintWidth > other.x &&
        y < other.y + other.footprint_height &&
        y + footprintHeight > other.y,
    )
  )
    return 'Another building needs this space. Choose a clear spot.';
  return undefined;
}

export function placementMatches(settlement: BuildSettlement, draft: PlacementDraft): boolean {
  const building = settlement.buildings.find((item) => item.id === draft.buildingId);
  return (
    !!building &&
    building.x === draft.x &&
    building.y === draft.y &&
    building.rotation === draft.rotation &&
    building.district_code === draft.districtCode
  );
}

export function placementErrorMessage(error: unknown): string {
  if (error instanceof BrowserApiError) {
    const messages: Record<string, string> = {
      placement_stale:
        'Your city changed. Refresh the layout and review this spot before saving again.',
      placement_occupied:
        'Another building now occupies this spot. Refresh the layout and choose a clear spot.',
      placement_outside_district: 'Keep the whole building inside the highlighted district.',
      district_locked: 'That district is not unlocked yet.',
      building_not_placeable: 'Finish construction before moving this building.',
      journey_interaction_locked: 'Building changes are paused while you are moving too fast.',
    };
    if (messages[error.code]) return messages[error.code]!;
  }
  return 'We could not confirm the move. Refresh the layout to check it before trying again.';
}

export async function loadPlacementLayout(signal?: AbortSignal): Promise<BuildSettlement> {
  const response = await browserApiClient().GET('/api/v1/settlement', {
    signal: signal
      ? AbortSignal.any([signal, AbortSignal.timeout(15000)])
      : AbortSignal.timeout(15000),
  });
  return requiredApiData(response.data, response.error, 'Your settlement could not load.');
}

export async function savePlacement(settlement: BuildSettlement, draft: PlacementDraft) {
  if (!navigator.onLine) throw new Error('Reconnect before saving your building position.');
  const problem = placementProblem(settlement, draft);
  if (problem) throw new Error(problem);
  const building = settlement.buildings.find((item) => item.id === draft.buildingId)!;
  const body = {
    x: draft.x,
    y: draft.y,
    district_code: draft.districtCode,
    rotation: draft.rotation,
    expected_layout_version: settlement.layout_version,
  };
  const response = await browserApiClient({ mutation: true }).PATCH(
    '/api/v1/settlement/buildings/{building_id}/placement',
    {
      params: { path: { building_id: building.id } },
      body,
      signal: AbortSignal.timeout(15000),
    },
  );
  return requiredApiData(response.data, response.error, 'The building could not move.');
}
